// Desempenho do campus no celular (B29-M6): as decisões, sem DOM.
//
// No desktop nada disso muda: o laço segue na taxa da tela, o devicePixelRatio é o do navegador e
// as telas seguem a configuração. No celular, o laço desenha no máximo 30 vezes por segundo, o
// devicePixelRatio tem teto em 2 (a 3 vezes mais pixels sem nitidez que se note a essa distância)
// e, com a bateria baixa e sem carregar, as telas dos computadores ficam paradas.
//
// Quem liga isso ao navegador é campus.ts (laço, dpr, aba oculta) e mobile.ts (Battery API).
// Testado em tests/desempenho.test.ts.

/** Modo das telas dos computadores (ThemeSettings.screens). */
export type Telas = 'anim' | 'still' | 'off';

/** Bateria do aparelho: nível de 0 a 1 e se está carregando. */
export interface Bateria {
  nivel: number;
  carregando: boolean;
}

/** Intervalo entre quadros no celular: 30 por segundo. */
export const INTERVALO_CELULAR_MS = 1000 / 30;
/**
 * Folga para o vsync: a tela de 60 Hz entrega quadros a cada 16,7 ms, então o segundo deles (33,3 ms)
 * tem que passar mesmo com um pouco de atraso, e não o terceiro (50 ms).
 */
const FOLGA_MS = 4;
/** Teto do devicePixelRatio no celular. */
export const DPR_MAX_CELULAR = 2;
/** Abaixo deste nível (0 a 1), sem carregar, as telas dos computadores ficam paradas. */
export const NIVEL_BATERIA_BAIXA = 0.2;

/**
 * Cadência do desenho. No desktop, todo quadro desenha. No celular, há um desenho a cada 1/30 s,
 * contado num relógio próprio: `proximo` é o instante do próximo desenho (0 no começo). Contar a
 * partir do último quadro desenhado faria um quadro que chega 3 ms cedo perder o seu turno e custar
 * 16,7 ms a mais. Se o laço ficou parado mais que um intervalo (aba oculta, aparelho cheio), recomeça
 * dali, sem tentar recuperar o tempo perdido. Devolve se este quadro desenha e o novo `proximo`.
 */
export function cadenciaDoDesenho(agora: number, proximo: number, celular: boolean): { desenha: boolean; proximo: number } {
  if (!celular) return { desenha: true, proximo };
  if (agora < proximo - FOLGA_MS) return { desenha: false, proximo };
  const base = agora - proximo > INTERVALO_CELULAR_MS ? agora : proximo;
  return { desenha: true, proximo: base + INTERVALO_CELULAR_MS };
}

/** devicePixelRatio que o campus usa para o canvas e para os tamanhos de texto e de pílula. */
export function dprDoCampo(dprNavegador: number, celular: boolean): number {
  const dpr = dprNavegador || 1;
  return celular ? Math.min(dpr, DPR_MAX_CELULAR) : dpr;
}

/**
 * Modo das telas dos computadores agora. Quadro fixo (`still`) é a escolha da pessoa e fica assim; com
 * a bateria baixa e sem carregar, o modo animado vira fixo no celular, e volta quando carrega ou sobe
 * de 20%. `desligado` continua desligado. Sem leitura de bateria (null), vale a configuração.
 */
export function telasEfetivas(configurado: Telas, bateria: Bateria | null, celular: boolean): Telas {
  if (configurado !== 'anim') return configurado;
  if (!celular || !bateria) return configurado;
  return bateria.nivel < NIVEL_BATERIA_BAIXA && !bateria.carregando ? 'still' : configurado;
}
